import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RefreshCw, MessageCircle, Sparkles, Flame } from "lucide-react";
import { Reveal } from "../components/Common";
import Seo from "../components/Seo";
import { BLEND_FINDER_QUESTIONS, BAKHOOR_BLENDS, IMAGES, waLink } from "../mock/mock";

const scoreBlends = (answers) => {
  const keywords = answers.flatMap((a) => (a ? a.keywords : []));
  return BAKHOOR_BLENDS.map((b) => {
    const notes = b.notes.toLowerCase();
    let score = 0;
    keywords.forEach((k) => {
      if (notes.includes(k.toLowerCase())) score += 1;
    });
    return { ...b, score };
  }).sort((a, b) => b.score - a.score);
};

const BlendFinder = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(Array(BLEND_FINDER_QUESTIONS.length).fill(null));
  const [done, setDone] = useState(false);

  const total = BLEND_FINDER_QUESTIONS.length;
  const progress = done ? 100 : Math.round((step / total) * 100);

  const choose = (option) => {
    const next = [...answers];
    next[step] = option;
    setAnswers(next);
    setTimeout(() => {
      if (step + 1 < total) setStep(step + 1);
      else setDone(true);
    }, 220);
  };

  const restart = () => {
    setAnswers(Array(total).fill(null));
    setStep(0);
    setDone(false);
  };

  const ranked = done ? scoreBlends(answers) : [];
  const top = ranked[0];
  const runnersUp = ranked.slice(1, 4).filter((r) => r.score > 0);

  return (
    <div className="bg-noir min-h-screen">
      <Seo
        title="Bakhoor Blend Finder — Find Your Signature Oud Scent | Meilleur"
        description="Answer four quick questions and discover your signature bakhoor blend, matched from Meilleur's 13+ hand-blended oud fragrances. Free personalised oud recommendation."
        path="/blend-finder"
        keywords="bakhoor finder, oud scent quiz, find your oud, signature bakhoor, oud recommendation, fragrance finder"
      />
      {/* Intro band */}
      <section className="relative pt-40 pb-16 overflow-hidden">
        <div className="max-w-[1360px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="eyebrow mb-5 flex items-center gap-3">
              <Sparkles size={15} className="text-[var(--green)]" /> The Blend Finder
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] max-w-3xl text-[var(--green-deep)]">
              Discover your <span className="gold-text italic">signature</span> bakhoor.
            </h1>
            <p className="mt-6 text-[var(--muted)] text-lg font-body font-light max-w-xl">
              Answer four short questions and we'll match you to a blend from our signature range.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {/* progress */}
          <div className="flex items-center justify-between mb-3 text-xs tracking-[0.16em] uppercase text-[var(--muted)]">
            <span>{done ? "Your match" : `Question ${step + 1} of ${total}`}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1 w-full bg-[var(--cream-2)] rounded-full overflow-hidden mb-12">
            <div className="h-full bg-[var(--green)] transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>

          {!done ? (
            <div key={step} className="reveal in-view">
              <h2 className="font-serif-display text-3xl md:text-4xl mb-10 text-[var(--green-deep)]">
                {BLEND_FINDER_QUESTIONS[step].q}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {BLEND_FINDER_QUESTIONS[step].options.map((opt) => {
                  const selected = answers[step]?.label === opt.label;
                  return (
                    <button
                      key={opt.label}
                      onClick={() => choose(opt)}
                      className={`text-left p-6 border rounded transition-all duration-300 group ${
                        selected
                          ? "border-[var(--green)] bg-[rgba(20,81,60,0.06)]"
                          : "border-[var(--line)] bg-[var(--paper)] hover:border-[var(--green)] hover:-translate-y-1"
                      }`}
                    >
                      <span className="font-serif-display text-2xl text-[var(--green-deep)] group-hover:text-[var(--green)] transition-colors">
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <button onClick={() => setStep(step - 1)} className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[var(--muted)] hover:text-[var(--green)] transition-colors">
                  <ArrowLeft size={14} /> Previous
                </button>
              )}
            </div>
          ) : (
            <div className="reveal in-view">
              <div className="lux-card overflow-hidden">
                <div className="img-zoom h-64 rounded-none">
                  <img src={IMAGES.bakhoorBowl} alt={top.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-12">
                  <div className="eyebrow mb-4 flex items-center gap-2"><Flame size={14} className="text-[var(--green)]" /> Your Signature Blend</div>
                  <h2 className="font-serif-display text-4xl md:text-5xl mb-2 gold-text">{top.name}</h2>
                  <p className="text-[var(--muted)] font-body mb-7">Blend No. {top.n} · matched to your preferences</p>
                  <div className="hairline opacity-40 mb-7" />
                  <div className="eyebrow text-[0.6rem] mb-4">Fragrance Notes</div>
                  <div className="flex flex-wrap gap-2.5">
                    {top.notes.split(",").map((n) => (
                      <span key={n} className="border border-[var(--line)] px-4 py-2 text-sm text-[var(--muted)] font-body rounded-sm">{n.trim()}</span>
                    ))}
                  </div>
                  <div className="mt-9 flex flex-wrap gap-4">
                    <a href={waLink(`the "${top.name}" bakhoor blend`)} target="_blank" rel="noopener noreferrer" className="btn-gold">
                      <MessageCircle size={15} /> Enquire on WhatsApp
                    </a>
                    <Link to="/bakhoor" className="btn-ghost">See All Blends <ArrowRight size={15} /></Link>
                  </div>
                </div>
              </div>

              {runnersUp.length > 0 && (
                <div className="mt-10">
                  <div className="eyebrow mb-5">You may also love</div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {runnersUp.map((r) => (
                      <div key={r.n} className="border border-[var(--line)] bg-[var(--paper)] p-6 rounded">
                        <span className="font-serif-display text-xl text-[var(--green-deep)]">{r.name}</span>
                        <p className="text-xs text-[var(--muted)] font-body mt-2 leading-relaxed">{r.notes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={restart} className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.16em] uppercase text-[var(--muted)] hover:text-[var(--green)] transition-colors">
                <RefreshCw size={14} /> Start over
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlendFinder;
